import { Loader2, FileText, Clock } from 'lucide-react';
import type { WageReport } from '../../../api/staff';

interface WageReportPanelProps {
  startDate: string;
  endDate: string;
  setStartDate: (d: string) => void;
  setEndDate: (d: string) => void;
  report: WageReport | null;
  loading: boolean;
  onGenerate: () => void;
  viewMode: 'summary' | 'detail';
  setViewMode: (m: 'summary' | 'detail') => void;
}

function WageReportPanel({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  report,
  loading,
  onGenerate,
  viewMode,
  setViewMode,
}: WageReportPanelProps) {
  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-4">
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
            />
          </div>
          <button
            onClick={onGenerate}
            disabled={loading}
            className="bg-moss-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-moss-700 transition disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            Generate Report
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-stone-500 dark:text-stone-400">Quick:</span>
            <button
              onClick={() => {
                const d = new Date(); d.setDate(1);
                setStartDate(d.toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
              }}
              className="text-xs px-2 py-1 border rounded hover:bg-stone-50 dark:hover:bg-[#242d28] text-stone-600 dark:text-stone-400"
            >
              This Month
            </button>
            <button
              onClick={() => {
                const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1);
                const start = d.toISOString().split('T')[0];
                const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
                setStartDate(start); setEndDate(end);
              }}
              className="text-xs px-2 py-1 border rounded hover:bg-stone-50 dark:hover:bg-[#242d28] text-stone-600 dark:text-stone-400"
            >
              Last Month
            </button>
            <button
              onClick={() => {
                const now = new Date(); const day = now.getDay();
                const start = new Date(now); start.setDate(now.getDate() - day);
                const end = new Date(start); end.setDate(start.getDate() + 6);
                setStartDate(start.toISOString().split('T')[0]);
                setEndDate(end.toISOString().split('T')[0]);
              }}
              className="text-xs px-2 py-1 border rounded hover:bg-stone-50 dark:hover:bg-[#242d28] text-stone-600 dark:text-stone-400"
            >
              This Week
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-12 flex flex-col items-center justify-center">
          <Loader2 size={32} className="animate-spin text-moss-600 mb-4" />
          <p className="text-stone-600 dark:text-stone-400 font-medium">Generating wage report...</p>
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
              <p className="text-sm text-stone-600 dark:text-stone-400">Contractor Wages Owed</p>
              <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">
                ${report.report.totalWages.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
              <p className="text-sm text-stone-600 dark:text-stone-400">Workshops</p>
              <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{report.report.workshopCount}</p>
              {report.report.unstaffedWorkshops > 0 && (
                <p className="text-xs text-clay-500 mt-0.5">{report.report.unstaffedWorkshops} unstaffed</p>
              )}
            </div>
            <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
              <p className="text-sm text-stone-600 dark:text-stone-400">Staff Assigned</p>
              <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{report.report.staffCount}</p>
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                {report.report.contractorCount} contractor{report.report.contractorCount !== 1 ? 's' : ''} / {report.report.staffCount - report.report.contractorCount} full-time
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('summary')}
                className={`px-3 py-2 text-sm ${viewMode === 'summary' ? 'bg-moss-600 text-white' : 'bg-white dark:bg-[#1a1f1c] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#242d28]'}`}
              >
                Summary by Staff
              </button>
              <button
                onClick={() => setViewMode('detail')}
                className={`px-3 py-2 text-sm ${viewMode === 'detail' ? 'bg-moss-600 text-white' : 'bg-white dark:bg-[#1a1f1c] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#242d28]'}`}
              >
                Detailed Line Items
              </button>
            </div>
          </div>

          {viewMode === 'summary' ? (
            <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 dark:bg-[#1e2520] border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Staff Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Positions</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Workshops</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Wages Owed</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {report.summary.map((row) => {
                    const isSalaried = row.staffType === 'full-time';
                    return (
                      <tr key={row.contractorId} className="hover:bg-stone-50 dark:hover:bg-[#242d28]">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="font-medium text-bark-900 dark:text-[#e8ece9]">{row.name}</div>
                              <div className="text-sm text-stone-500 dark:text-stone-400">{row.email}</div>
                            </div>
                            {isSalaried && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Full-time</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {row.positions.map((pos) => (
                              <span key={pos} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pos === 'teacher' ? 'bg-moss-100 text-moss-700' : 'bg-water-100 text-water-700'}`}>
                                {pos}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-bark-900 dark:text-[#e8ece9]">{row.workshopCount}</td>
                        <td className="px-6 py-4 text-right">
                          {isSalaried ? (
                            <span className="text-sm text-purple-500 italic">Salaried</span>
                          ) : (
                            <span className={`text-sm font-semibold ${row.totalWages > 0 ? 'text-bark-900 dark:text-[#e8ece9]' : 'text-clay-500'}`}>
                              ${row.totalWages.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {row.totalWages === 0 && <span className="ml-1 text-xs font-normal">(no rate set)</span>}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {report.summary.length > 0 && (
                    <tr className="bg-stone-50 dark:bg-[#1e2520] font-semibold">
                      <td className="px-6 py-4 text-bark-900 dark:text-[#e8ece9]" colSpan={3}>Total Contractor Wages</td>
                      <td className="px-6 py-4 text-right text-bark-900 dark:text-[#e8ece9]">
                        ${report.report.totalWages.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {report.summary.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="mx-auto text-stone-400 mb-4" size={48} />
                  <p className="text-stone-600 dark:text-stone-400">No workshop assignments found in this date range</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 dark:bg-[#1e2520] border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Workshop</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Staff</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Position</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Rate</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">Wages</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {report.lineItems.map((item, idx) => {
                    const isSalaried = item.staffType === 'full-time';
                    const isUnstaffed = item.staffType === 'unstaffed';
                    return (
                      <tr key={idx} className={`hover:bg-stone-50 dark:hover:bg-[#242d28] ${isUnstaffed ? 'bg-clay-50/50 dark:bg-clay-900/10' : ''}`}>
                        <td className="px-6 py-3 text-sm text-bark-900 dark:text-[#e8ece9]">
                          {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-6 py-3">
                          <div className="text-sm text-bark-900 dark:text-[#e8ece9]">{item.workshopTitle.replace('Bonsai Bar @ ', '')}</div>
                          <div className="text-xs text-stone-500 dark:text-stone-400">{item.venue}</div>
                        </td>
                        <td className="px-6 py-3 text-sm text-bark-900 dark:text-[#e8ece9]">
                          {isUnstaffed ? (
                            <span className="text-clay-500 italic text-xs">Unstaffed</span>
                          ) : (
                            <>
                              {item.staffName}
                              {isSalaried && (
                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">FT</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          {!isUnstaffed && item.position && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.position === 'teacher' ? 'bg-moss-100 text-moss-700' : 'bg-water-100 text-water-700'}`}>
                              {item.position}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-bark-900 dark:text-[#e8ece9]">
                          {isUnstaffed ? '' : isSalaried ? <span className="text-purple-500 italic">Salaried</span> : item.rate > 0 ? `$${item.rate}` : '—'}
                        </td>
                        <td className="px-6 py-3 text-right text-sm font-medium text-bark-900 dark:text-[#e8ece9]">
                          {isUnstaffed ? '' : isSalaried ? <span className="text-purple-500 italic">—</span> : `$${item.wages.toFixed(2)}`}
                        </td>
                      </tr>
                    );
                  })}
                  {report.lineItems.length > 0 && (
                    <tr className="bg-stone-50 dark:bg-[#1e2520] font-semibold">
                      <td className="px-6 py-3" colSpan={4}>Total Contractor Wages</td>
                      <td className="px-6 py-3" />
                      <td className="px-6 py-3 text-right text-sm">${report.report.totalWages.toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {report.lineItems.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="mx-auto text-stone-400 mb-4" size={48} />
                  <p className="text-stone-600 dark:text-stone-400">No workshop assignments found in this date range</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-12 text-center">
          <FileText className="mx-auto text-stone-400 mb-4" size={48} />
          <p className="text-stone-600 dark:text-stone-400">Select a date range and click &quot;Generate Report&quot; to view wages owed</p>
        </div>
      )}
    </div>
  );
}

export default WageReportPanel;
